// One shared feed of "what is waiting for the admin".
//
// The sidebar badges, the header indicator and the Today screen all read this
// context, so they can never disagree — and it costs one request, not three.
// It refreshes every 60s and again whenever the admin comes back to the tab,
// so a number on screen is never stale enough to act on wrongly.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { worklistApi } from '../api/worklistApi';

const REFRESH_MS = 60_000;

const WorklistContext = createContext({
  data: null,
  loading: false,
  error: null,
  refresh: () => {},
  countFor: () => 0,
});

export const WorklistProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Survives re-renders so a slow response can't overwrite a newer one, and so
  // unmounting mid-flight doesn't set state on a dead component.
  const alive = useRef(true);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const res = await worklistApi.get();
      if (alive.current) {
        setData(res.data);
        setError(null);
      }
    } catch (e) {
      // A failed badge fetch must never break the page it decorates — keep the
      // last good numbers and let the Today screen surface the error.
      if (alive.current) {
        setError(e?.response?.data?.error || e?.message || 'Could not load the work list');
      }
    } finally {
      inFlight.current = false;
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    const onFocus = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      alive.current = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh]);

  const countFor = useCallback(
    (key) => Number(data?.queues?.[key]?.count || 0),
    [data],
  );

  const value = React.useMemo(
    () => ({ data, loading, error, refresh, countFor }),
    [data, loading, error, refresh, countFor],
  );

  return <WorklistContext.Provider value={value}>{children}</WorklistContext.Provider>;
};

export const useWorklist = () => useContext(WorklistContext);

export default useWorklist;
